import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Copy, Check } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';

const CampaignDetailView = ({ campaign, teamId }) => {
  const [activeTab, setActiveTab] = useState('timeline');
  const [timeRange, setTimeRange] = useState('today');
  const [timelineData, setTimelineData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [exportSuccess, setExportSuccess] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);

  const utmId = campaign?.utm?.utm_id || campaign?.utmId || '';
  const resolvedTeamId = React.useMemo(() => {
    if (teamId) {
      return String(teamId);
    }
    if (campaign?.teamId) {
      return String(campaign.teamId);
    }
    if (campaign?.team?._id) {
      return String(campaign.team.id || campaign.team._id);
    }
    if (campaign?.team) {
      return String(campaign.team);
    }
    return '';
  }, [campaign?.team, campaign?.teamId, teamId]);
  const campaignName = campaign?.name || 'Campaign';
  const longUrl = campaign?.fullUrl || campaign?.full_url || '';
  const shortUrl = campaign?.shortUrl || campaign?.short_url || '';

  const copyToClipboard = async (text, type) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedUrl(type);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'timeline' && utmId && resolvedTeamId) {
      fetchTimelineData();
    }
  }, [activeTab, timeRange, utmId, resolvedTeamId]);

  useEffect(() => {
    if (activeTab === 'rawData' && utmId && resolvedTeamId) {
      fetchRawData();
    }
  }, [activeTab, utmId, resolvedTeamId]);

  const fetchTimelineData = async () => {
    if (!utmId || !resolvedTeamId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `/utm-events/analytics/${encodeURIComponent(utmId)}/timeline`,
        {
          params: {
            timeRange,
            teamId: resolvedTeamId,
          },
        },
      );

      if (response.data?.success) {
        const formatted = (response.data.graphData || []).map((item) => ({
          time: formatTimeLabel(item.time, timeRange),
          count: item.count,
          fullTime: item.time,
        }));
        setTimelineData(formatted);
      } else {
        setTimelineData([]);
      }
    } catch (err) {
      console.error('Error fetching campaign timeline data:', err);
      setError('Failed to load timeline data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRawData = async () => {
    if (!utmId || !resolvedTeamId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `/utm-events/analytics/${encodeURIComponent(utmId)}/raw`,
        {
          params: {
            teamId: resolvedTeamId,
          },
        },
      );

      if (response.data?.success) {
        setRawData(response.data.rawData || []);
      } else {
        setRawData([]);
      }
    } catch (err) {
      console.error('Error fetching campaign raw data:', err);
      setError('Failed to load raw data');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (index) => {
    const nextExpanded = new Set(expandedRows);
    if (nextExpanded.has(index)) {
      nextExpanded.delete(index);
    } else {
      nextExpanded.add(index);
    }
    setExpandedRows(nextExpanded);
  };

  const truncateDistinctId = (distinctId, startLength = 8, endLength = 8) => {
    if (!distinctId || typeof distinctId !== 'string') {
      return distinctId || 'Unknown';
    }
    if (distinctId.length <= startLength + endLength) {
      return distinctId;
    }
    return `${distinctId.substring(0, startLength)}...${distinctId.substring(distinctId.length - endLength)}`;
  };

  const formatTimeLabel = (isoString, range) => {
    const date = new Date(isoString);

    switch (range) {
      case 'today':
      case '24h':
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case '7d':
      case '30d':
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      case '3m':
      case '1y':
        return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
      default:
        return date.toLocaleTimeString();
    }
  };

  const getLabelInterval = (dataLength) => {
    if (dataLength <= 7) return 0;
    if (dataLength <= 14) return 1;
    if (dataLength <= 30) return 2;
    return Math.ceil(dataLength / 10);
  };

  const exportToCSV = () => {
    if (!rawData.length) {
      return;
    }

    const headers = ['Distinct ID', 'Session ID', 'Geography', 'DateTime', 'isWeb3User', 'Wallet Address'];
    const rows = rawData.map((item) => {
      const distinctId = `"${String(item.distinctId ?? '').replace(/"/g, '""')}"`;
      const sessionId = `"${String(item.sessionId ?? '').replace(/"/g, '""')}"`;
      const geography = `"${String(item.geography ?? '').replace(/"/g, '""')}"`;
      const datetime = `"${new Date(item.datetime).toLocaleString().replace(/"/g, '""')}"`;
      const web3 = `"${item.isWeb3User ? 'true' : 'false'}"`;
      const wallet = `"${String(item.walletAddress ?? 'N/A').replace(/"/g, '""')}"`;
      return [distinctId, sessionId, geography, datetime, web3, wallet].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
    const filenameSafe = String(campaignName).replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const filename = `${filenameSafe}_utm_${utmId || 'unknown'}_${dateStr}_${timeStr}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 3000);
  };

  const styles = {
    primarycolor: "#0f172a",
    textSecondary: "#666666",
    borderColor: "#E5E7EB",
    backgroundColor: "#ffffff"
  };

  return (
    <div className="bg-white" style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Tabs */}
      <div
        style={{
          borderBottom: `1px solid ${styles.borderColor}`,
          marginBottom: '24px'
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 0,
            padding: 0,
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', gap: 0 }}>
            {[
              { key: 'timeline', label: 'Timeline Graph' },
              { key: 'rawData', label: 'Raw Data' }
            ].map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    border: 'none',
                    outline: 'none',
                    borderRadius: 0,
                    padding: '8px 16px',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: 'transparent',
                    color: isActive ? styles.primaryColor : styles.textSecondary,
                    position: 'relative',
                    borderBottom: isActive ? `2px solid ${styles.primaryColor}` : '2px solid transparent',
                    marginBottom: '-1px',
                    fontFamily: 'Raleway, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.target.style.color = styles.primaryColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.target.style.color = styles.textSecondary;
                    }
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          {/* URL Copy Buttons */}
          <div className="flex items-center gap-2">
            {shortUrl && (
              <button
                onClick={() => copyToClipboard(shortUrl, 'short')}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors border border-gray-300 bg-white hover:bg-gray-50"
                style={{ fontFamily: 'Raleway, sans-serif' }}
              >
                {copiedUrl === 'short' ? (
                  <>
                    <Check size={16} className="text-green-600" />
                    Short
                  </>
                ) : (
                  <>
                    <Copy size={16} style={{ color: '#666666' }} />
                    Short
                  </>
                )}
              </button>
            )}
            {longUrl && (
              <button
                onClick={() => copyToClipboard(longUrl, 'long')}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors border border-gray-300 bg-white hover:bg-gray-50"
                style={{ fontFamily: 'Raleway, sans-serif' }}
              >
                {copiedUrl === 'long' ? (
                  <>
                    <Check size={16} className="text-green-600" />
                    Long
                  </>
                ) : (
                  <>
                    <Copy size={16} style={{ color: '#666666' }} />
                    Long
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div>
        {activeTab === 'timeline' ? (
          <div>
            <div className="flex items-center mb-6">
              {[
                { value: 'today', label: 'Today' },
                { value: '24h', label: '24H' },
                { value: '7d', label: '7D' },
                { value: '30d', label: '30D' },
                { value: '3m', label: '3M' },
                { value: '1y', label: '1Y' },
              ].map((option, index) => (
                <button
                  key={option.value}
                  onClick={() => setTimeRange(option.value)}
                  className={`time-selector-normal px-3 py-1 text-sm font-medium transition-colors ${
                    index === 0 ? 'rounded-l-lg' : ''
                  } ${
                    index === 5 ? 'rounded-r-lg' : ''
                  } ${
                    timeRange === option.value
                      ? 'bg-gray-100 text-[#0f172a] border border-gray-300 z-10'
                      : 'bg-white text-[#475569] hover:bg-gray-50 border border-gray-300'
                  } ${
                    index > 0 ? '-ml-px' : ''
                  }`}
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center h-96">
                <LiquidFillLoader size={120} />
                <span className="mt-4 text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Loading chart...
                </span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <div className="text-[#DC2626] text-sm mb-2">{error}</div>
                  <button onClick={fetchTimelineData} className="text-sm text-[#1d0c46] underline">
                    Try again
                  </button>
                </div>
              </div>
            ) : timelineData.length === 0 ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center text-sm text-[#475569]">No data available for this time range</div>
              </div>
            ) : (
              <div className="h-96 bg-white border border-[#E5E7EB] p-4 rounded-lg">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timelineData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                    <defs>
                      <linearGradient id="campaignLineGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1d0c46" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#1d0c46" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical horizontal />
                    <XAxis
                      dataKey="time"
                      interval={getLabelInterval(timelineData.length)}
                      tick={{
                        fill: '#475569',
                        fontSize: 11,
                        fontFamily: 'Raleway, sans-serif',
                      }}
                      axisLine={{ stroke: '#D1D5DB' }}
                      label={{
                        value: 'Time',
                        position: 'insideBottom',
                        offset: -10,
                        style: {
                          textAnchor: 'middle',
                          fontSize: '12px',
                          fill: '#0f172a',
                          fontWeight: '500',
                        },
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: '#475569',
                        fontSize: 11,
                        fontFamily: 'Raleway, sans-serif',
                      }}
                      axisLine={{ stroke: '#D1D5DB' }}
                      label={{
                        value: 'Event Count',
                        angle: -90,
                        position: 'insideLeft',
                        offset: 0,
                        style: {
                          textAnchor: 'middle',
                          fontSize: '12px',
                          fill: '#0f172a',
                          fontWeight: '500',
                        },
                      }}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div
                              className="p-3 border border-[#E5E7EB] bg-white shadow-lg rounded-lg"
                              style={{ fontFamily: 'Raleway, sans-serif' }}
                            >
                              <p className="text-sm font-semibold text-[#1F2937] mb-1">
                                {payload[0].payload.time}
                              </p>
                              <p className="text-sm text-[#475569]">
                                Events:{' '}
                                <span className="font-semibold text-[#1F2937]">{payload[0].value}</span>
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#1d0c46"
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                      isAnimationActive
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        ) : (
          <div>
            {rawData.length > 0 && (
              <div className="flex items-center justify-between mb-4">
                <div />
                <button
                  onClick={exportToCSV}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors flex-shrink-0"
                  style={{
                    fontFamily: 'Raleway, sans-serif',
                    backgroundColor: '#ffffff',
                    color: '#0f172a',
                    border: '1px solid #DDE2EE'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#DDE2EE';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }}
                >
                  <Download size={16} />
                  Export Data
                </button>
              </div>
            )}

            {exportSuccess && (
              <div
                className="fixed top-4 right-4 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg z-[9999] flex items-center gap-2 transition-all duration-300 ease-in-out"
                style={{ fontFamily: 'Raleway, sans-serif' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Raw data exported successfully</span>
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center h-64">
                <LiquidFillLoader size={100} />
                <span className="mt-4 text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Loading data...
                </span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="text-[#DC2626] text-sm mb-2">{error}</div>
                  <button onClick={fetchRawData} className="text-sm text-[#1d0c46] underline">
                    Try again
                  </button>
                </div>
              </div>
            ) : rawData.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center text-sm text-[#475569]">No raw data found for this campaign</div>
              </div>
            ) : (
              <div className="overflow-x-auto border border-[#E5E7EB] rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[#F9FAFB]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                        Distinct ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                        Session ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                        Geography
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                        DateTime
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rawData.map((item, index) => {
                      const isExpanded = expandedRows.has(index);
                      const rowKey = `${item.sessionId || 'session'}-${item.datetime}-${index}`;

                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            className="hover:bg-[#F9FAFB] transition-colors cursor-pointer"
                            onClick={() => toggleRow(index)}
                          >
                            <td className="px-4 py-3 text-sm text-[#1F2937] font-mono">
                              <div className="flex items-center gap-2">
                                <span className="text-[#9CA3AF] flex-shrink-0">
                                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </span>
                                <span title={item.distinctId}>
                                  {truncateDistinctId(item.distinctId)}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#475569] font-mono">{item.sessionId}</td>
                            <td className="px-4 py-3 text-sm text-[#475569]">{item.geography}</td>
                            <td className="px-4 py-3 text-sm text-[#475569]">
                              {new Date(item.datetime).toLocaleString()}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-[#F9FAFB]">
                              <td colSpan="4" className="px-4 py-4">
                                <div className="pl-6 border-l-2 border-[#1d0c46]">
                                  <div className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2">
                                    Event Metadata
                                  </div>
                                  <div className="space-y-2 text-sm text-[#1F2937]">
                                    <div className="flex items-start gap-3">
                                      <span className="font-medium min-w-[120px]">Distinct ID:</span>
                                      <span className="text-[#475569] font-mono break-words">
                                        {item.distinctId || 'Not provided'}
                                      </span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                      <span className="font-medium min-w-[120px]">isWeb3User:</span>
                                      <span className="text-[#475569]">
                                        {item.isWeb3User ? 'true' : 'false'}
                                      </span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                      <span className="font-medium min-w-[120px]">walletAddress:</span>
                                      <span className="text-[#475569] break-words">
                                        {item.walletAddress || 'Not provided'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignDetailView;
